"use client";

import { useMemo, useState } from "react";
import Link from "next/link";

// The supplements index adopts the charts' strict two-tone print palette: ink
// and dark red on warm paper, with diagonal hatch standing in for a missing
// figure — so the page reads like the research it indexes rather than a
// generic card grid.
const RED = "#8b0000";
const INK = "#111111";

export type ExplorerEntry = {
  slug: string;
  title: string;
  author?: string;
  description: string;
  searchText: string;
  imageUrl?: string;
  section: string;
};

const hashSlug = (slug: string): number => {
  let hash = 0;
  for (let i = 0; i < slug.length; i++) hash = (hash * 31 + slug.charCodeAt(i)) | 0;
  return Math.abs(hash);
};

const coverInitial = (title: string): string =>
  (title.replace(/^\[[^\]]*\]\s*/, "").trim()[0] || "?").toUpperCase();

// A hatch "plate" for supplements with no exported figure: diagonal ink/red
// bars (the two-tone dither used across the charts) behind the initial.
const HatchCover = ({ entry, idPrefix }: { entry: ExplorerEntry; idPrefix: string }) => {
  const useRed = hashSlug(entry.slug) % 3 === 0;
  const stroke = useRed ? RED : INK;
  const patternId = `hatch-${idPrefix}-${entry.slug}`;
  return (
    <div className="relative w-full h-full">
      <svg className="w-full h-full" preserveAspectRatio="none" aria-hidden="true">
        <defs>
          <pattern id={patternId} width="8" height="8" patternTransform="rotate(45)" patternUnits="userSpaceOnUse">
            <rect width="8" height="8" fill="#fffff8" />
            <line x1="0" y1="0" x2="0" y2="8" stroke={stroke} strokeWidth="2.2" opacity="0.85" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill={`url(#${patternId})`} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span
          className="font-sans font-semibold leading-none bg-[#fffff8] px-2.5 py-1 border"
          style={{ color: stroke, borderColor: stroke }}
        >
          {coverInitial(entry.title)}
        </span>
      </div>
    </div>
  );
};

// Every cover is a framed figure: contained (never cropped) on a paper ground
// with padding, so each supplement's chart/diagram keeps its own composition.
const Cover = ({ entry, idPrefix, className }: { entry: ExplorerEntry; idPrefix: string; className?: string }) => (
  <div className={`overflow-hidden border border-black/80 bg-[#fffff8] transition-all duration-200 ease-out group-hover:border-[#8b0000] group-hover:shadow-[0_0_0_2.5px_#8b0000] ${className ?? ""}`}>
    {entry.imageUrl ? (
      // eslint-disable-next-line @next/next/no-img-element
      <img src={entry.imageUrl} alt="" className="w-full h-full object-contain" />
    ) : (
      <HatchCover entry={entry} idPrefix={idPrefix} />
    )}
  </div>
);

const SectionTag = ({ section }: { section: string }) => (
  <span className="font-mono text-[10px] md:text-[11px] uppercase tracking-[0.1em] text-[#8b0000] whitespace-nowrap">
    {section}
  </span>
);

// The lead supplement: a large cover with the headline beneath it. It shares
// the top of the page with the feature rail (right column on lg+), and its
// cover flattens to 2:1 there so the whole headline block fits short, wide
// windows instead of pushing everything else below the fold.
const HeroCard = ({ entry }: { entry: ExplorerEntry }) => (
  <Link href={`/supplements/${entry.slug}`} className="group no-underline flex flex-col">
    <Cover entry={entry} idPrefix="hero" className="w-full aspect-[3/2] md:aspect-[2/1] p-4" />
    <div className="mt-4 md:mt-5 flex flex-col">
      <SectionTag section={entry.section} />
      <h2 className="text-3xl md:text-[34px] leading-[1.08] mt-1.5 mb-0 transition-colors group-hover:text-[#8b0000]">
        {entry.title}
      </h2>
      <p className="text-base md:text-lg leading-[1.45] text-black/70 mt-3 mb-0 line-clamp-2 font-normal">
        {entry.description}
      </p>
      {entry.author && <div className="text-sm italic text-black/55 mt-3">{entry.author}</div>}
      <span className="inline-block mt-3 italic text-[#8b0000] group-hover:underline">
        Read the supplement <span className="font-sans not-italic">→</span>
      </span>
    </div>
  </Link>
);

// A compact horizontal row for the feature rail beside the hero: thumbnail on
// the left, title and a short description on the right. On md+ each row
// stretches to its third of the hero's height and the thumbnail fills the
// row, so the four headline covers tile the block with no leftover corner.
const FeatureRow = ({ entry }: { entry: ExplorerEntry }) => (
  <Link
    href={`/supplements/${entry.slug}`}
    className="group no-underline grid grid-cols-[104px_1fr] md:grid-cols-[2fr_3fr] gap-4 md:gap-5 items-start md:items-stretch md:h-full"
  >
    <Cover entry={entry} idPrefix="feature" className="w-full aspect-[3/2] md:aspect-auto p-1.5 md:p-2.5" />
    <div className="flex flex-col md:self-center">
      <SectionTag section={entry.section} />
      <h3 className="not-italic text-lg md:text-xl leading-[1.15] mt-1 mb-0 transition-colors group-hover:text-[#8b0000]">
        {entry.title}
      </h3>
      <p className="text-sm md:text-[15px] leading-[1.4] text-black/65 mt-1.5 mb-0 line-clamp-2 font-normal">
        {entry.description}
      </p>
    </div>
  </Link>
);

// The default card, used for every supplement in the browse-all grid.
const SupplementCard = ({ entry, idPrefix }: { entry: ExplorerEntry; idPrefix: string }) => (
  <Link href={`/supplements/${entry.slug}`} className="group no-underline flex flex-col">
    <Cover entry={entry} idPrefix={idPrefix} className="w-full aspect-[16/10] p-3" />
    <div className="mt-3.5 flex flex-col">
      <SectionTag section={entry.section} />
      <h3 className="not-italic text-xl md:text-[23px] leading-[1.15] mt-1.5 mb-0 transition-colors group-hover:text-[#8b0000]">
        {entry.title}
      </h3>
      <p className="text-[15px] md:text-base leading-[1.45] text-black/65 mt-2 mb-0 line-clamp-2 font-normal">
        {entry.description}
      </p>
      {entry.author && <div className="text-xs md:text-sm italic text-black/55 mt-2.5">{entry.author}</div>}
    </div>
  </Link>
);

const FilterChip = ({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    className={`text-[15px] leading-none px-3.5 py-2 border transition-colors ${
      active
        ? "bg-[#8b0000] text-[#fffff8] border-[#8b0000]"
        : "border-black text-black hover:border-[#8b0000] hover:text-[#8b0000]"
    }`}
  >
    {label}
  </button>
);

export default function SupplementsExplorer({
  entries,
  headlineSlugs,
  sections,
}: {
  entries: ExplorerEntry[];
  headlineSlugs: string[];
  sections: string[];
}) {
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  const bySlug = useMemo(() => new Map(entries.map((entry) => [entry.slug, entry])), [entries]);
  const headline = headlineSlugs.map((slug) => bySlug.get(slug)).filter((entry): entry is ExplorerEntry => Boolean(entry));
  const [hero, ...features] = headline;

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return entries.filter((entry) => {
      if (activeSection && entry.section !== activeSection) return false;
      return !needle || entry.searchText.includes(needle);
    });
  }, [entries, activeSection, query]);

  return (
    <div className="text-black">
      {/* Headline: the lead supplement on the left, with the remaining
          featured supplements stacked as a compact rail beside it (below it on
          narrow screens) — so wide-but-short windows still show all four. */}
      {hero && (
        <section className="pb-10 border-b-2 border-black">
          <div className="grid grid-cols-1 md:grid-cols-[1.7fr_1fr] gap-9 md:gap-8 lg:gap-10 xl:gap-12">
            <HeroCard entry={hero} />
            {features.length > 0 && (
              <div className="flex flex-col gap-6 md:gap-0 md:border-l md:border-black md:pl-6 lg:pl-8 xl:pl-10">
                {features.map((entry, index) => (
                  <div
                    key={entry.slug}
                    className={`md:flex-1 md:min-h-0 ${index > 0 ? "border-t border-black pt-6 md:pt-5" : ""} ${
                      index < features.length - 1 ? "md:pb-5" : ""
                    }`}
                  >
                    <FeatureRow entry={entry} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Browse all: filter settings + card grid. */}
      <section className="mt-14">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5 mb-11">
          <div className="flex flex-wrap items-center gap-2.5">
            <FilterChip label="All" active={activeSection === null} onClick={() => setActiveSection(null)} />
            {sections.map((section) => (
              <FilterChip
                key={section}
                label={section}
                active={activeSection === section}
                onClick={() => setActiveSection(activeSection === section ? null : section)}
              />
            ))}
          </div>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search supplements…"
            className="text-base px-1 py-1.5 bg-transparent border-0 border-b border-black rounded-none focus:border-[#8b0000] outline-none w-full md:w-60 placeholder:text-black/40 placeholder:italic"
          />
        </div>

        {filtered.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-8 gap-y-12">
            {filtered.map((entry) => (
              <SupplementCard key={entry.slug} entry={entry} idPrefix="grid" />
            ))}
          </div>
        ) : (
          <p className="font-mono text-sm text-black/50 py-8">No supplements match that search.</p>
        )}

        <div className="mt-10 font-mono text-xs text-black/45">
          {filtered.length} of {entries.length} supplements
          {activeSection ? ` · ${activeSection}` : ""}
        </div>
      </section>
    </div>
  );
}
